import { useEffect, useRef } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { useStore } from '@/store/useStore';
import postCustomerPreferences from '@/api/customer-data/post-customer-preferences';
import { setCustomerPreferences } from '@/store/actions';
import { CustomerPreferences } from '@/store/slices/customerData/types';

/** Opt-in status before it resolves (v2: `-2: not visible, 0: false, 1+: multiplier`). */
const OPT_IN_NOT_VISIBLE = -2;
/** Counter caps at 3, mirroring the backend increment guard (process-preferences.js). */
const DYB_POPUP_COUNTER_MAX = 3;

/**
 * The popup boolean is shown from the mount `/features` response. For an eligible
 * new SWJ player this hook increments the counter once and POSTs it to
 * `/preferences`, then updates the store from the response so the counter (and any
 * flag the backend recomputes) stays in sync for the next launch.
 */
export const useDybPopupCounter = () => {
  const {
    hasActiveSession,
    isCustomerDataLoading,
    isDybFeatureFlagEnabled,
    jackpotData,
    jackpotOptInStatus,
    showDYBPopupCounter,
  } = useStore(
    useShallow((s) => ({
      hasActiveSession: s.userSessionContext.hasActiveSession,
      isCustomerDataLoading: s.customerData.loading,
      isDybFeatureFlagEnabled: s.swj.isDybFeatureFlagEnabled,
      jackpotData: s.swj.jackpotData,
      jackpotOptInStatus: s.swj.jackpotOptInStatus,
      showDYBPopupCounter: s.customerPreferences.showDYBPopupCounter,
    })),
  );

  const isSWJGame = Boolean(jackpotData?.provider?.uid);
  const isOptInReady =
    jackpotOptInStatus !== undefined &&
    jackpotOptInStatus !== OPT_IN_NOT_VISIBLE;
  const isOptedIn = Boolean(jackpotOptInStatus);

  // `!isCustomerDataLoading` waits for the default /features call to land the
  // server counter; the store defaults it to 0, so reading early would reset a
  // returning player back into the popup.
  const shouldPersistCounter =
    hasActiveSession &&
    !isCustomerDataLoading &&
    isSWJGame &&
    isDybFeatureFlagEnabled &&
    isOptInReady &&
    !isOptedIn;

  const hasPersistedCounter = useRef(false);

  useEffect(() => {
    if (hasPersistedCounter.current || !shouldPersistCounter) {
      return;
    }
    hasPersistedCounter.current = true;

    const counter = showDYBPopupCounter ?? 0;
    const nextCounter = counter < DYB_POPUP_COUNTER_MAX ? counter + 1 : counter;

    const persistCounter = async () => {
      const response = await postCustomerPreferences({
        showDYBPopupCounter: nextCounter,
      });
      const preferences = (response as { data?: CustomerPreferences })?.data;
      if (preferences) {
        setCustomerPreferences(preferences);
        return;
      }
      // POST failed (fetcher resolves the error object, so there's no `data`).
      // Release the latch so a later eligible render can retry the advance.
      hasPersistedCounter.current = false;
    };

    persistCounter();
  }, [shouldPersistCounter, showDYBPopupCounter]);
};
